/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useContext } from "react";
import { withRouter } from "react-router-dom";
import EnhancedTable from "components/common_components/Tables/EnhancedTable";
import Page from "components/common_components/Page/Page";
import Form from "./Form";
import { useDispatch, useSelector } from "react-redux";
import { notifySuccess, notifyError, notifyInfo } from "redux/actions/Notifications";
import LoadingPage from "components/common_components/LoadingPage";
import SettingsContext from "../SettingsContext";
import { updateUser } from "redux/actions/Login";
import { hasElements } from "utils/arrays";
import Header from "components/common_components/Header";
import Icon from "components/common_components/Icon";
import Button from "components/common_components/Button";

const headCells = [
    { index: 1, id: "code", disablePadding: false, label: "Code" },
    { index: 2, id: "name", disablePadding: true, label: "Name" },
    { index: 3, id: "description", disablePadding: true, label: "Description" },
    { index: 4, id: "prefix", disablePadding: true, label: "Prefix" },
];

function Modules(props) {
    const dispatch = useDispatch();
    const { user } = useSelector((state) => state.Login);
    const [isLoading, setIsLoading] = useState(false);
    const [data, setData] = useState([]);
    const [mode, setMode] = useState("listOnly");
    const [itemToEdit, setItemToEdit] = useState({});
    const [pageableData, setPageableData] = useState(undefined);
    const defaultRequestData = {
        filter: {},
        orderBy: "code",
        orderDirection: "asc",
        pageNumber: 0,
        pageSize: 5,
    };

    const { Modules, Users } = useContext(SettingsContext);

    function getModules(requestData = defaultRequestData, showLoader = true) {
        if (showLoader) {
            setIsLoading(true);
        }
        Modules.getList({
            onSuccess: (data, pageableData) => {
                setData(data);
                setPageableData(pageableData);
                setIsLoading(false);
            },
            onInfo: (info, pageableData) => {
                dispatch(notifyInfo(info));
                setPageableData(pageableData);
                setIsLoading(false);
            },
            onError: (error) => {
                dispatch(notifyError(error));
                setIsLoading(false);
            },
            data: requestData,
        });
    }

    useEffect(() => {
        getModules();

        return () => {
            setData([]);
            setItemToEdit({});
        };
    }, []);

    function onRowClickHandler(item) {
        setItemToEdit(item);
        setMode("update");
    }

    function onCancelHandler() {
        setItemToEdit({});
        setMode("listOnly");
    }

    async function onSuccess(_, setSubmitting) {
        setSubmitting(false);
        onCancelHandler();
        dispatch(notifySuccess(`Module ${mode === "add" ? "created" : "updated"} successfully!`));
        getModules();
        try {
            const refreshedUser = await Users.getOneByIdAsync(user.id);
            if (refreshedUser) {
                dispatch(updateUser(refreshedUser));
            }
        } catch (error) {}
    }

    function onError(error, setSubmitting, setErrors) {
        if (error && error.errors) {
            setErrors(error.errors);
            dispatch(notifyError(error.message));
        } else {
            onCancelHandler();
            dispatch(notifyError(error));
        }
        setSubmitting(false);
    }

    function capitalize(string) {
        string = string.trim().toLowerCase();
        const firstLetter = string.charAt(0).toUpperCase();
        return firstLetter + string.slice(1);
    }

    async function onSubmitHandler(values, { setSubmitting, setErrors }) {
        const { code, name, prefix, path, formName, calendarColor, eventFields, ...payload } = values;

        const apiCall = mode === "add" ? Modules.add : Modules.update;

        let calendarSettings = {};

        if (calendarColor && String(calendarColor).includes("#")) {
            calendarSettings.calendarColor = calendarColor;
        }

        if (eventFields && typeof eventFields === "object" && Object.values(eventFields).some((x) => Boolean(x) === true)) {
            const markedEventFields = Object.keys(eventFields).reduce((arr, key) => {
                return Boolean(eventFields[key]) ? [...arr, key] : arr;
            }, []);
            if (hasElements(markedEventFields)) {
                calendarSettings.calendarFields = markedEventFields.join(",");
            }
        }

        apiCall({
            payload: {
                id: mode === "update" ? itemToEdit.id : null,
                ...payload,
                code: code.trim().toUpperCase(),
                name: capitalize(name),
                prefix: prefix.trim().toUpperCase(),
                path: path.trim().toLowerCase(),
                formName: capitalize(formName),
                ...calendarSettings,
            },
            onSuccess: (response) => onSuccess(response, setSubmitting),
            onError: (error) => onError(error, setSubmitting, setErrors),
        });
    }

    function onDeleteSuccess() {
        dispatch(notifyInfo("Removed successfully!"));
        getModules();
    }

    function onDeleteError(error) {
        dispatch(notifyError(error.message));
    }

    function onDeleteHandler(selectedItems) {
        Modules.deleteAll({
            ids: selectedItems,
            onSuccess: onDeleteSuccess,
            onError: onDeleteError,
        });
    }

    function filterBuilder(filterValue) {
        return filterValue
            ? {
                  code: filterValue,
                  name: filterValue,
                  description: filterValue,
                  prefix: filterValue,
              }
            : {};
    }

    function onAddHandler() {
        setItemToEdit(undefined);
        setMode("add");
    }

    return (
        <Page>
            {isLoading ? (
                <LoadingPage />
            ) : (
                <>
                    {mode !== "listOnly" && (
                        <Form
                            data={itemToEdit}
                            mode={mode}
                            onSubmit={onSubmitHandler}
                            onCancel={onCancelHandler}
                            title={mode === "add" ? "New Module" : "Update Module"}
                        />
                    )}
                    {mode === "listOnly" && (
                        <>
                            <Header title="Modules">
                                <Button primary onClick={onAddHandler}>
                                    <Icon name="add" size="md" color="white" />
                                    <span>Add</span>
                                </Button>
                            </Header>
                            <div className="layout">
                                <EnhancedTable
                                    headCells={headCells}
                                    data={data}
                                    defaultOrder={{
                                        order: defaultRequestData.orderDirection,
                                        orderBy: defaultRequestData.orderBy,
                                    }}
                                    selectable
                                    keyColumnName="id"
                                    onRowClick={onRowClickHandler}
                                    onDelete={onDeleteHandler}
                                    onRequestData={getModules}
                                    filterBuilder={filterBuilder}
                                    pageableData={pageableData}
                                />
                            </div>
                        </>
                    )}
                </>
            )}
        </Page>
    );
}

export default withRouter(Modules);
